
import React, { useEffect } from "react";
import {
  Mail, Phone, MapPin, ArrowUpRight, Globe,
  GraduationCap, ShieldCheck, Target, Eye,
  Lightbulb, Code2, Server, Database,
  Network, Cpu, ExternalLink, CalendarDays,
  Linkedin, Github, ChevronRight, Sparkles
} from "lucide-react";
import { motion } from "framer-motion";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 35 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.7, ease: "easeOut" }
  }
};

const socialLinks = [
  { name: "Google Scholar", url: "#", color: "bg-blue-600" },
  { name: "ResearchGate", url: "#", color: "bg-green-600" },
  { name: "ORCID", url: "#", color: "bg-gray-800" },
  { name: "Scopus", url: "#", color: "bg-orange-500" },
  { name: "Web of Science", url: "#", color: "bg-purple-600" }
];

const expertise = [
  { icon: Server, title: "Server Administration", desc: "Enterprise server infrastructure and system management." },
  { icon: Database, title: "Database Management", desc: "Secure, optimized and scalable database solutions." },
  { icon: Network, title: "Network Infrastructure", desc: "Reliable network architecture and connectivity." },
  { icon: Code2, title: "Software Development", desc: "Modern web applications and university automation." },
  { icon: ShieldCheck, title: "Cyber Security", desc: "Information security and institutional data protection." },
  { icon: Cpu, title: "Digital Transformation", desc: "Smart campus solutions and digital innovation." }
];

export default function Itd({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Information Technology Division | Bandarban University"
}) {
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <div className="overflow-hidden bg-[#f6f9f7] text-gray-800">

      {/* HERO */}
      <section className="relative min-h-[490px] flex items-center overflow-hidden bg-[#073b2a] text-white">
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: "radial-gradient(#9ee7be 1px, transparent 1px)",
            backgroundSize: "30px 30px"
          }}
        />

        <motion.div
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 18, repeat: Infinity }}
          className="absolute -right-32 -top-40 w-[550px] h-[550px] rounded-full border border-white/10"
        />

        <div className="absolute right-10 bottom-[-180px] w-[400px] h-[400px] rounded-full bg-emerald-400/20 blur-[100px]" />

        <div className="relative max-w-7xl mx-auto px-6 py-24 w-full grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-emerald-200 text-sm mb-7">
              <Sparkles size={16} />
              Bandarban University
            </div>

            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
              Information Technology
              <span className="block text-emerald-300">Division</span>
            </h1>

            <p className="text-gray-300 text-lg leading-8 max-w-xl">
              Empowering education through technology, innovation and
              digital transformation. Building a smarter and more
              connected university ecosystem.
            </p>

            <div className="flex flex-wrap gap-4 mt-9">
              <a href="#consultant"
                className="px-7 py-3.5 rounded-xl bg-emerald-400 text-[#073b2a] font-bold hover:bg-white transition flex items-center gap-2">
                Meet Our Consultant <ArrowUpRight size={19}/>
              </a>
              <a href="#contact"
                className="px-7 py-3.5 rounded-xl border border-white/30 hover:bg-white/10 transition">
                Contact IT Division
              </a>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            className="hidden lg:flex justify-center relative"
          >
            <div className="relative w-[360px] h-[360px] rounded-full border border-emerald-300/30 flex items-center justify-center">
              <div className="absolute inset-7 rounded-full border border-white/20"/>
              <div className="w-64 h-64 rounded-full bg-gradient-to-br from-emerald-300 to-teal-700 flex items-center justify-center shadow-2xl shadow-emerald-500/20">
                <div className="text-center">
                  <Globe size={85} className="mx-auto text-white mb-3"/>
                  <p className="font-bold text-xl">Digital Campus</p>
                  <p className="text-sm text-white/80">Technology • Innovation</p>
                </div>
              </div>
              {[
                { Icon: Code2, pos: "top-0 left-1/2 -translate-x-1/2" },
                { Icon: Database, pos: "bottom-7 left-5" },
                { Icon: Network, pos: "bottom-7 right-5" }
              ].map(({Icon, pos}, i) => (
                <motion.div
                  key={i}
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 3, repeat: Infinity, delay: i * .5 }}
                  className={`absolute ${pos} w-14 h-14 rounded-2xl bg-white text-emerald-800 shadow-xl flex items-center justify-center`}
                >
                  <Icon size={25}/>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 -mt-8">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 grid sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
          {[
            { icon: ShieldCheck, title: "Secure Systems", text: "Reliable IT Infrastructure" },
            { icon: Lightbulb, title: "Smart Innovation", text: "Technology-driven Solutions" },
            { icon: Globe, title: "Digital Campus", text: "Connected University" }
          ].map((item, i) => (
            <div key={i} className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                <item.icon size={24}/>
              </div>
              <div>
                <h3 className="font-bold">{item.title}</h3>
                <p className="text-sm text-gray-500">{item.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CONSULTANT PROFILE */}
      <section id="consultant" className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible"
            viewport={{ once: true }} variants={fadeUp}
            className="text-center mb-14"
          >
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Leadership & Expertise
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold mt-3 text-gray-900">
              Meet Our IT Consultant
            </h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
              Guiding institutional technology, infrastructure and
              digital transformation.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 45 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: .8 }}
            className="grid lg:grid-cols-5 bg-white rounded-[30px] shadow-xl overflow-hidden border border-gray-100"
          >
            {/* PHOTO */}
            <div className="lg:col-span-2 relative min-h-[400px] bg-gradient-to-br from-emerald-900 to-teal-700">
              <div className="absolute inset-0 opacity-20"
                style={{backgroundImage:"radial-gradient(white 1px,transparent 1px)",backgroundSize:"20px 20px"}}/>
              <img
                src="/assets/images/Yusuf-Khalil.jpg"
                alt="Md Yusuf Khalil"
                className="relative w-full h-full min-h-[400px] object-cover object-top"
              />
              <div className="absolute bottom-5 left-5 right-5 p-4 rounded-2xl bg-white/90 backdrop-blur-md">
                <p className="font-bold text-gray-900">Md Yusuf Khalil</p>
                <p className="text-sm text-emerald-800 font-medium">IT Consultant</p>
              </div>
            </div>

            {/* DETAILS */}
            <div className="lg:col-span-3 p-7 md:p-12">
              <span className="inline-flex items-center gap-2 text-emerald-700 text-sm font-bold uppercase tracking-wider">
                <GraduationCap size={18}/> Professional Profile
              </span>

              <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mt-4">
                Md Yusuf Khalil
              </h2>
              <p className="text-emerald-700 font-semibold mt-2 text-lg">
                IT Consultant | Bandarban University
              </p>

              <p className="text-gray-600 leading-8 mt-6">
                Providing strategic technology consultancy and digital
                infrastructure solutions to support the university's
                academic and administrative operations. Focused on
                developing secure, scalable and innovative IT systems
                for a modern digital campus.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mt-8">
                <a href="tel:+8801979420300"
                  className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition">
                  <Phone className="text-emerald-700"/>
                  <div>
                    <p className="text-xs text-gray-500">Phone</p>
                    <p className="font-semibold text-sm">+880 1979420300</p>
                  </div>
                </a>
                <a href="mailto:yusufkhalilemon@gmail.com"
                  className="flex items-center gap-3 p-4 rounded-xl bg-gray-50 hover:bg-emerald-50 transition">
                  <Mail className="text-emerald-700"/>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-semibold text-sm break-all">yusufkhalilemon@gmail.com</p>
                  </div>
                </a>
              </div>

              <div className="flex flex-wrap gap-2 mt-8">
                {socialLinks.map((link) => (
                  <a key={link.name} href={link.url}
                    target="_blank" rel="noreferrer"
                    className={`${link.color} text-white text-xs font-semibold px-4 py-2.5 rounded-lg hover:-translate-y-1 hover:shadow-lg transition`}>
                    {link.name} <ExternalLink size={12} className="inline ml-1"/>
                  </a>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* EXPERTISE */}
      <section className="py-20 bg-white px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Technology & Expertise
            </h2>
            <p className="text-gray-500 mt-4">
              Supporting the university through modern technology services.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {expertise.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{opacity:0,y:30}}
                whileInView={{opacity:1,y:0}}
                viewport={{once:true}}
                transition={{delay:i*.08}}
                whileHover={{y:-8}}
                className="group p-7 rounded-2xl border border-gray-100 bg-[#f8fbf9] hover:bg-[#0b4933] hover:text-white hover:shadow-2xl transition duration-300"
              >
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-800 flex items-center justify-center group-hover:bg-white/20 group-hover:text-white transition">
                  <item.icon size={27}/>
                </div>
                <h3 className="text-xl font-bold mt-6">{item.title}</h3>
                <p className="text-gray-500 group-hover:text-gray-200 mt-3 leading-7 text-sm">
                  {item.desc}
                </p>
                <div className="mt-5 text-emerald-700 group-hover:text-emerald-300 flex items-center gap-2 text-sm font-bold">
                  Explore Capability <ChevronRight size={17}/>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION MISSION */}
      <section className="py-24 px-6 bg-[#f1f7f3]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-emerald-700 font-bold uppercase tracking-[3px] text-sm">
              Our Direction
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold mt-3">
              Vision, Mission & Goals
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-7">
            {[
              {
                icon: Eye, title: "Our Vision",
                color: "bg-blue-600",
                text: "To become a leading institution of higher learning in Asia."
              },
              {
                icon: Target, title: "Our Mission",
                color: "bg-emerald-600",
                text: "To nurture creativity, research, and leadership through quality education."
              },
              {
                icon: Lightbulb, title: "Our Goals",
                color: "bg-orange-500",
                text: "Expanding research opportunities and strengthening global partnerships."
              }
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{opacity:0,y:30}}
                whileInView={{opacity:1,y:0}}
                viewport={{once:true}}
                transition={{delay:i*.15}}
                whileHover={{y:-8}}
                className="bg-white p-8 rounded-3xl shadow-md hover:shadow-xl transition"
              >
                <div className={`w-14 h-14 ${item.color} rounded-2xl text-white flex items-center justify-center`}>
                  <item.icon size={28}/>
                </div>
                <h3 className="text-2xl font-bold mt-6">{item.title}</h3>
                <p className="text-gray-500 leading-8 mt-4">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="relative overflow-hidden bg-[#073b2a] text-white py-20 px-6">
        <div className="absolute w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl -right-20 -top-20"/>
        <div className="relative max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-emerald-300 font-bold uppercase tracking-[3px] text-sm">
                Get In Touch
              </span>
              <h2 className="text-3xl md:text-5xl font-extrabold mt-4 leading-tight">
                Let's Build a Smarter Digital Campus
              </h2>
              <p className="text-gray-300 mt-5 leading-8">
                Connect with the Information Technology Division for
                technology support, digital solutions and institutional
                IT services.
              </p>
              <a href="mailto:admin@bubban.ac.bd"
                className="inline-flex items-center gap-3 mt-8 bg-emerald-400 text-[#073b2a] font-bold px-7 py-4 rounded-xl hover:bg-white transition">
                Contact IT Division <ArrowUpRight size={20}/>
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur-lg border border-white/10 rounded-3xl p-7 space-y-6">
              <div className="flex gap-4 items-start">
                <MapPin className="text-emerald-300 shrink-0" size={23}/>
                <div>
                  <h4 className="font-bold">University Address</h4>
                  <p className="text-gray-300 text-sm mt-2 leading-7">
                    K.B Road, Shualok, Post Office: Shualok - 4600,
                    Bandarban Sadar, Bangladesh
                  </p>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <Phone className="text-emerald-300"/>
                <div>
                  <h4 className="font-bold">Phone</h4>
                  <a href="tel:+8801318305900" className="text-gray-300 text-sm">+880 1841420300</a>
                </div>
              </div>
              <div className="flex gap-4 items-center">
                <Mail className="text-emerald-300"/>
                <div>
                  <h4 className="font-bold">Email</h4>
                  <a href="mailto:admin@bubban.ac.bd" className="text-gray-300 text-sm">admin@bubban.ac.bd</a>
                </div>
              </div>
              <div className="border-t border-white/20 pt-5">
                <p className="text-xs text-gray-400">
                  INFORMATION TECHNOLOGY DIVISION
                </p>
                <p className="font-bold mt-1">Bandarban University</p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
